/* eslint-disable no-param-reassign */
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import SparkMD5 from 'spark-md5';
import moment from 'moment';

import XMLFormParser from 'apps/web_forms/XMLFormParser';
import { API_BASE_PATH } from 'apps/web_forms/utils/constants';
import { getFormattedDate } from 'apps/web_forms/utils/dateTimeFormatter';
import { MultiDispatchError } from 'apps/web_forms/utils/errors';
import _ from 'lodash';
import { createFieldsWithIndexes } from 'apps/web_forms/utils/arrays'
import { FormTypes } from 'apps/web_forms/utils/constants';

const BASE_URL = `${window.location.origin}${API_BASE_PATH}`;
const isSubmission = window.location.href.includes('submission');

export const GET_ALL_FORMS_URL_JSON = `${BASE_URL}/author_forms.json`; // /api/forms.json
export const SUBMIT_FORM_URL = `${BASE_URL}/submissions.xml`; // /api/submissions.xml
export const UPLOAD_IMAGE_URL = `${BASE_URL}/submission_images`; // /api/submission_images
export const RESOURCES_URL = `${BASE_URL}/organization_resources.xml`; // /api/organization_resources.xml
export const RESOURCE_URL = `${BASE_URL}/organization_resources/`; // /api/organization_resources
export const GET_FORM_URL = `${BASE_URL}/author_forms/`; // /api/forms
export const IMAGE_MD5_URL = `${BASE_URL}/submission_image_md5`; // /api/submission_image_md5
export const GET_DISPATCH_CATEGORIES_URL = `${BASE_URL}/author_dispatch_categories.json/`; // /api/dispatch_categories
export const GET_ALL_DRAFTS_URL = `${BASE_URL}/author_drafts.json`; // /api/drafts.json
export const GET_DRAFT_URL = `${BASE_URL}/author_drafts/`; // /api/drafts
export const DELETE_DRAFT_URL = GET_DRAFT_URL; // /api/drafts
export const POST_DRAFT_URL = GET_DRAFT_URL; // /api/drafts
export const GET_SUBMISSION_URL = `${BASE_URL}/webstore_submissions/`; // /api/forms
export const PUT_SUBMIT_SUBMISSION_URL = `${BASE_URL}/webstore_submissions/`; // /api/submissions.xml

// react-query unique keys
export const GET_DISPATCH_CATEGORIES = 'getDispatchCategories';

function getCSRFToken() {
  const metaTag = document.querySelector("meta[name='csrf-token']");
  if (metaTag) return metaTag.getAttribute('content');
  return null;
}

export const isPublicForm = () => {
  return !!window.location.pathname.match('/web_forms/shared_form/');
};

export function getPublicFormUuid() {
  if (isPublicForm()) {
    return window.location.pathname.split('/web_forms/shared_form/').last();
  }
  return '';
}

const commonHeaders = {
  Pragma: 'no-cache',
  'X-CSRF-Token': getCSRFToken(),
  'X-DEVICEMAGIC-PUBLIC-FORM-UUID': getPublicFormUuid(),
  'X-DEVICEMAGIC-ISSUBMISSION': isSubmission ? "TRUE" : "FALSE",
};

const generalHeaders = {
  ...commonHeaders,
  'Cache-Control': 'no-cache',
  Accept: 'application/xml; charset=utf-8',
  'Content-Type': 'application/xml; charset=utf-8',
};

const jsonHeaders = {
  ...commonHeaders,
  'Cache-Control': 'no-cache',
  Accept: 'application/json; charset=utf-8',
  'Content-Type': 'application/json; charset=utf-8',
};

const imageRequestHeaders = {
  ...commonHeaders,
  'Content-Type': 'image/png',
  Accept: '*/*',
};

const md5RequestHeaders = {
  ...commonHeaders,
  Accept: '*/*',
};

/**
 * Get Form List
 */
export const getAllForms = async () => {
  const response = await axios({
    method: 'GET',
    url: GET_ALL_FORMS_URL_JSON,
    headers: generalHeaders,
  });

  const { forms = [] } = response.data || {};
  
  return forms;
};

/**
 * Get Dispatches List
 */
export const getAllDispatches = async () => {
  const response = await axios({
    method: 'GET',
    url: GET_ALL_FORMS_URL_JSON,
    headers: generalHeaders,
  });

  const { forms = [] } = response.data || {};

  return forms
};

/**
 * Get a singular form by Id
 * @param {string|number} formId - the form id
 * @param {string} formVersion - the form version
 */
export const getForm = async ({ formId, formVersion, submissionId }) => {
  let response = null;
  const formVersionParam = formVersion ? `?version=${formVersion}` : '';
  const apiUrl = isSubmission ? `${GET_SUBMISSION_URL}${submissionId}.xml` : `${GET_FORM_URL}/${formId}.xml${formVersionParam}`

  try {
    response = await axios({
      method: 'GET',
      url: apiUrl,
      headers: generalHeaders,
    });

    const xmlNode = new DOMParser().parseFromString(response.data, 'text/xml');
    
    const xmlFormParser = new XMLFormParser(xmlNode);

    if (!xmlFormParser.result.inputList) {
      return {};
    }

    return xmlFormParser.result;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('There was an error on the request', error);
    return response;
  }
};

/**
 *
 * @param {string} inputsXml
 * @param formNamespace
 * @param formVersion
 */
const getSubmissionXml = (inputsXml, formNamespace, formVersion) => {
  const dateFormatted = getFormattedDate();
  return `
    <instance
      writeTime="${dateFormatted}"
      xmlns="${formNamespace}"
      formVersion="${formVersion}"
      submissionIdentifier="${uuidv4()}"
    >
      <inputs>${inputsXml}</inputs>
    </instance>
  `;
};

/**
 *
 * @param { userFormData } userFormData
 * @param { formNamespace } formNamespace
 */
export const submitForm = async ({
  finalXMLString,
  formNamespace,
  formVersion,
  submissionId
}) => {
  const formXml = getSubmissionXml(finalXMLString, formNamespace, formVersion);
  return submitFormFullXml(formXml, submissionId);
};

/**
 *
 * @param { userFormData } userFormData
 * @param { formNamespace } formNamespace
 */
export const submitDraft = async ({
  finalXMLString,
  formNamespace,
  formVersion,
  description,
  isUpdateExisting,
  draftId,
  resourceVersions,
}) => {
  const formXml = getSubmissionXml(finalXMLString, formNamespace, formVersion);
  return submitDraftFullXml(
    formXml,
    description,
    resourceVersions,
    isUpdateExisting,
    draftId
  );
};

/**
 *
 * @param {AxiosRequestConfig.method} method
 * @param {string} url
 * @param data
 * @param {AxiosRequestConfig.headers} [headers]
 */
const makeCallAndCheckErrors = async (method, url, data, headers) => {
  let response = null;
  try {
    response = await axios({
      headers: headers || generalHeaders,
      method,
      url,
      data,
    });
  } catch (error) {
    // TODO: Add bugsnag
    // eslint-disable-next-line no-console
    console.log('There was an error on the request', error);
    response = error.response;
    if (error?.response?.status === 422 && error.response.data?.error?.toLowerCase()?.includes('dispatch')) {
      const msg = `${error.response.status}: ${error.response.statusText}\n${error.response.data?.error}`;
      throw new MultiDispatchError(`Cause of MultiDispatchError:\n ${msg}`); 
    } else {
      throw error;
    }
  }
  return response;
};

/**
 *
 * @param { string } fullFormXmlString
 */
const submitFormFullXml = async (fullFormXmlString, submissionId) => {
  const data = {
    payload: fullFormXmlString,
  };
  const isSubmission = window.location.href.includes('submission');
  const apiUrl = isSubmission ? `${PUT_SUBMIT_SUBMISSION_URL}${submissionId}.xml` : SUBMIT_FORM_URL
  const action = isSubmission ? 'PUT' : 'POST'

  return makeCallAndCheckErrors(action, apiUrl, data, jsonHeaders);
};

/**
 *
 * @param {string} fullDraftXmlString
 * @param {string} description
 * @param {Array} resourceVersions
 * @param {boolean} isUpdateExisting
 * @param {number | string} [draftId]
 */
const submitDraftFullXml = async (
  fullDraftXmlString,
  description = null,
  resourceVersions = [],
  isUpdateExisting = false,
  draftId
) => {
  const method = isUpdateExisting ? 'PUT' : 'POST';
  const url = buildSaveDraftUrl(isUpdateExisting, draftId);
  const data = {
    description,
    resource_versions: resourceVersions,
    payload: fullDraftXmlString,
  };
  return makeCallAndCheckErrors(method, url, data, jsonHeaders);
};

/**
 *
 * @param {string} draftToSave
 * @param {string} draftDescription
 * @param {Array} resourceVersions
 */
export const saveNewDraft = async (
  draftToSave,
  draftDescription,
  resourceVersions = []
) => {
  return submitDraftFullXml(draftToSave, draftDescription, resourceVersions);
};

/**
 *
 * @param {boolean} isUpdateExisting
 * @param {number | string} draftId
 * @returns string
 */
const buildSaveDraftUrl = (isUpdateExisting, draftId) => {
  let draft = POST_DRAFT_URL;
  if (isUpdateExisting && draftId) {
    draft = `${draft}${draftId}`;
  }
  return draft;
};

/**
 * Get Draft List
 */
export const getAllDrafts = async () => {
  const response = await axios({
    method: 'GET',
    url: GET_ALL_DRAFTS_URL,
    headers: generalHeaders,
  });

  const { drafts = [] } = response.data || {};
  
  return drafts;
};

/**
 * Get a singular draft by Id
 * @param {draftId} draftId
 */
export const getDraftXMLString = async (draftId) => {
  let response = null;
  try {
    response = await axios({
      method: 'GET',
      url: `${GET_DRAFT_URL}${draftId}.xml`,
      headers: generalHeaders,
    });
    return response.data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('There was an error on the request', error);
    return response;
  }
};

const itemHasProperty = (item, property) =>
  Object.prototype.hasOwnProperty.call(item, property);

export const getRepeatGroupIdentifierWithoutSuffix = (identifier) =>
  identifier.replace(/\|\d+$/, '');

const createFieldNamesArray = (fields, fieldNamesArray = []) => {
 
  fields.forEach((field) => {
    if (field.children) {
      fieldNamesArray.push(field);
      fieldNamesArray = createFieldNamesArray(field.children, fieldNamesArray);
    } else {
      fieldNamesArray.push(field);
    }
  }, []);
  return fieldNamesArray;
};

/**
 * Get a singular draft by Id and returns an array
 * @param {draftId} draftId
 */
export const getDraftArray = async (draftId, form) => {
  const result = await getDraftXMLString(draftId);
  const xmlNode = new DOMParser().parseFromString(result, 'text/xml');

  const xmlFormParser = new XMLFormParser(xmlNode, false);
  const formVersion = xmlNode?.getElementsByTagName('instance')[0]?.getAttribute('formVersion');

  /*
    Added fieldsWithIndexes to solve 'Save as draft not saving multi-nested repeat groups past 1 level deep' DME-497
    The generated fields id of form and draft answers did not match and this fixes the issue
  */
  const draftList = createFieldNamesArray(xmlFormParser.inputList)
  const formList = createFieldNamesArray(form.inputList)

  const mergedInputList = draftList.map(draftItem => {
    let item = draftItem
    formList.map(formItem => {
        if(formItem.identifier === draftItem.identifier){
            item = {...formItem, ...draftItem}
        }
    })
    return item
  })

  const draftInitialsAnswers = {};
  const fieldsWithIndexes = createFieldsWithIndexes(mergedInputList);
  
  /* 
    fieldsWithIndexes contains all the necessary fields filled in the draft 
    The first if condition is only used so that the application can understand what fields to add while creating the form
    while the other checks are the answers for the draft
  */
  fieldsWithIndexes.forEach((item) => {
    if(itemHasProperty(item, 'children')){
      let identifier = getRepeatGroupIdentifierWithoutSuffix(item.identifier)
      if(draftInitialsAnswers[identifier]){
        draftInitialsAnswers[identifier] = [...draftInitialsAnswers[identifier], item]
      }else{
        draftInitialsAnswers[identifier] = [item];
      }
    }
    if (itemHasProperty(item, 'initialAnswer')) {
      draftInitialsAnswers[item.identifier] = item.initialAnswer;
      if (itemHasProperty(item, 'originalIdentifier')) {
        draftInitialsAnswers[item.originalIdentifier] = item.initialAnswer;
      }
    }
    if (itemHasProperty(item, 'imageIdentifier')) {
      draftInitialsAnswers[item.identifier] = item.imageIdentifier;
    }
  })
  
  return {
    formVersion,
    draftInitialsAnswers,
  };
};

/**
 * Delete a singular draft by Id
 * @param {draftId} draftId
 */
export const deleteDraft = async (draftId) => {
  const response = await axios({
    method: 'DELETE',
    url: `${DELETE_DRAFT_URL}${draftId}.xml`,
    headers: generalHeaders,
  });

  return response;
};

export const deleteDrafts = async (draftsToDelete) => {
  const deleteResponsePromises = draftsToDelete.map((draftToDelete) =>
    deleteDraft(draftToDelete.identifier)
  );
  const deletesPromise = Promise.all(deleteResponsePromises);
  await deletesPromise;
};

/**
 * Loop through the Map and submit each image to server
 * Returns a Map like { FieldId: Uuid, ... }
 * @param {Object} multimediaMapValues
 */
export const submitImages = async (multimediaMapValues) => {
  const mapImageIdentifiers = await Object.keys(multimediaMapValues).reduce(
    async (currentValueP, key) => {
      const currentValue = await currentValueP;
      let responseSubmitImage = await submitImage({
        base64Image: multimediaMapValues[key],
      });

      // If md5 check fails then try upload again the image
      if (!responseSubmitImage) {
        responseSubmitImage = await submitImage({
          base64Image: multimediaMapValues[key],
        });
      }
      const returnedMap = { ...currentValue };
      if (responseSubmitImage) {
        returnedMap[key] = responseSubmitImage;
      } else {
        returnedMap.error = true;
      }
      return returnedMap;
    },
    Promise.resolve({})
  );
  return mapImageIdentifiers;
};

/**
 * Upload image to the server
 * @param {Object} base64Image String
 */
export const submitImage = async ({ base64Image }) => {
  const imageIdentifier = uuidv4().toUpperCase();
  const submissionIdentifier = uuidv4().toUpperCase();

  const blobImage = await (await fetch(base64Image)).blob();

  let response = null;
  try {
    response = await axios({
      method: 'POST',
      url: `${UPLOAD_IMAGE_URL}?image_identifier=${imageIdentifier}&submission_identifier=${submissionIdentifier}`,
      headers: imageRequestHeaders,
      data: blobImage,
    });

    if (response.status < 200 || response.status > 299) {
      return response;
    }
    const getMd5Response = await getMD5Image({ imageIdentifier });

    const localmd5 = await buildFileMD5Hash(blobImage);

    if (getMd5Response.status < 200 || getMd5Response.status > 299) {
      return null;
    }

    return localmd5 === getMd5Response.data ? imageIdentifier : response;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('There was an error on the request.', error);
    return null;
  }
};

/**
 * https://github.com/satazor/js-spark-md5#hash-a-file-incrementally
 * @param {BlobFile} file
 */
const buildFileMD5Hash = (file) => {
  return new Promise((resolve, reject) => {
    const blobSlice =
      File.prototype.slice ||
      File.prototype.mozSlice ||
      File.prototype.webkitSlice;

    const chunkSize = 2097152; // Read in chunks of 2MB
    const chunks = Math.ceil(file.size / chunkSize);
    let currentChunk = 0;
    const spark = new SparkMD5.ArrayBuffer();
    const fileReader = new FileReader();

    fileReader.onload = function (e) {
      spark.append(e.target.result); // Append array buffer
      currentChunk += 1;

      if (currentChunk < chunks) {
        loadNext();
      } else {
        resolve(spark.end());
      }
    };

    fileReader.onerror = function () {
      reject(new Error('Something went wrong'));
    };

    function loadNext() {
      const start = currentChunk * chunkSize;
      const end =
        start + chunkSize >= file.size ? file.size : start + chunkSize;

      fileReader.readAsArrayBuffer(blobSlice.call(file, start, end));
    }

    loadNext();
  });
};

/**
 * Get image MD5 from server
 * @param {Object} imageIdentifier String
 */
export const getMD5Image = async ({ imageIdentifier }) => {
  let response = null;
  try {
    response = await axios({
      method: 'GET',
      url: `${IMAGE_MD5_URL}?id=${imageIdentifier}`,
      headers: md5RequestHeaders,
    });

    return response;
  } catch (error) {
    // TODO: Add bugsnag
    // eslint-disable-next-line no-console
    console.log('There was an error on the request.', error);
    return response;
  }
};

/**
 * Get XML Organization Resources filtered by identifiers
 * @param {string[]} identifiers
 */
export const getDeviceResources = async (identifiers) => {
  try {
    const response = await axios({
      method: 'POST',
      url: RESOURCES_URL,
      headers: { ...jsonHeaders, Accept: 'application/xml; charset=utf-8' },
      data: JSON.stringify({ identifiers }),
    });
    return parseResourcesXML(
      new DOMParser().parseFromString(response.data, 'text/xml')
    );
  } catch (error) {
    // TODO: Integrate Bugsnag to log the errors in prod.
    // eslint-disable-next-line no-console
    console.log('There was an error on the request.', error);
    return null;
  }
};

/**
 * Get ArrayBuffer of resource
 * @param {Object} params
 * @param {String} params.resourceId
 * @param {boolean} params.generated
 * @param {CancelToken} params.cancelToken
 * @param {number} params.version
 */
export const getDeviceResource = async (params) => {
  const { resourceId, generated, cancelToken, version } = params;
  const generatedParam = generated ? '?generated=true' : '';
  let versionParam = version != null ? `&version=${version}` : '';
  if (!generated && version != null) {
    versionParam = `?version=${version}`;
  }
  try {
    const response = await axios({
      method: 'GET',
      url: `${RESOURCE_URL}/${resourceId}${generatedParam}${versionParam}`,
      headers: generalHeaders,
      responseType: `${generated ? 'json' : 'arraybuffer'}`,
      cancelToken,
    });
    return response.data;
  } catch (err) {
    // TODO: Integrate Bugsnag to log the errors in prod.
    // eslint-disable-next-line no-console
    console.log('There was an error on the request.', err);
    return null;
  }
};

const resourceRequests = {};

/**
 * Requests a resource and returns a Promise.
 * If the same resource is already fetching, it would use the same fetching, instead of doing a new one.
 * @param {string} qualifiedResourceId Qualified resource id
 *                   (as constructed by getQualifiedResourceId - hash identifier + version number)
 * @param {string} fetchId Numeric ID of the resource
 * @param {string} optionalVersion Version to fetch - if omitted, will fetch latest
 */
export const newCachedRequest = (
  { qualifiedResourceId,
    fetchId,
    optionalVersion,
    generated,
  }
) => {
  if (resourceRequests[qualifiedResourceId]) {
    return resourceRequests[qualifiedResourceId];
  }

  resourceRequests[qualifiedResourceId] = getDeviceResource({
    resourceId: fetchId,
    generated,
    version: optionalVersion,
  });
  resourceRequests[qualifiedResourceId].then(() => {
    delete resourceRequests[qualifiedResourceId];
  });

  return resourceRequests[qualifiedResourceId];
};

/**
 * Requests draft images and returns a Promise.
 * @param {string} draftId Id of the draft whose images are to be fetched
 * @returns {Promise} Promise of the draft images
 */
export const getDraftImages = async (draftId) => {
  const response = await axios({
    method: 'GET',
    url: `${GET_DRAFT_URL}${draftId}/file_urls`,
    headers: generalHeaders,
  });

  return parseXMLDraftImagesList(
    new DOMParser().parseFromString(response.data, 'text/xml')
  );
};

/**
 * Get dispatch categories (regular, geolocated, geofenced)
 * @returns {Promise} Promise of the dispatch categories
 */
export const getDispatchCategories = async () => {
  const response = await axios({
    method: 'GET',
    url: GET_DISPATCH_CATEGORIES_URL,
    headers: jsonHeaders,
  });
  return response.data;
};

/**
 * Get possible draft authors (devices and users)
 * @returns {Promise} Promise of the draft authors
 */
export const getDraftAuthors = async (draftId) => {
  const response = await axios({
    method: 'GET',
    url: `${GET_DRAFT_URL}${draftId}/authors`,
    headers: jsonHeaders,
  });
  return response.data;
};

/**
 *
 * @param {string} draftId
 * @param {string} authorableId
 * @param {string} authorableType
 */
export const reassignDraft = async (
  draftId,
  authorableId,
  authorableType
) => {
  try {
    const response = await axios({
      method: 'POST',
      url: `${GET_DRAFT_URL}${draftId}/reassign`,
      data: { authorable_id: authorableId, authorable_type: authorableType},
      headers: jsonHeaders
    });
    return response;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log('There was an error on the request.', error);
    return null;
  }
}

/**
 * Parse Resources XML and build a JS object
 * @param {XMLDocument} xmlNode
 */
const parseResourcesXML = (xmlNode) => {
  if (!xmlNode) {
    return null;
  }

  const resourcesNode = xmlNode.children[0].children;
  const resources = [];

  for (let i = 0; i < resourcesNode.length; i++) {
    const resourceNode = resourcesNode[i];
    const resourceItem = {};
    for (let j = 0; j < resourceNode.children.length; j++) {
      const attr = resourceNode.children[j];
      resourceItem[attr.nodeName] = attr.innerHTML;
    }
    resources.push(resourceItem);
  }
  return resources;
};

/**
 * parse XMLNode into a JS Array
 * @param {XMLNode} xmlNode
 * @param {string} childNodeName Text name of the child tags
 */
const parseXMLList = (xmlNode, childNodeName) => {
  const forms = [];
  const formsList = xmlNode.children[0];
  for (let i = 0; i < formsList.children.length; i++) {
    const node = formsList.children[i];
    if (node.nodeName === childNodeName) {
      const form = {};
      for (let e = 0; e < node.children.length; e++) {
        form[node.children[e].nodeName] = node.children[e].textContent;
      }
      form.isDispatch = form.is_oneshot === 'true';
      forms.push(form);
    }
  }

  return forms;
};

/**
 * parse XMLNode with list of forms into a JS Array
 * @param {XMLNode} xmlNode
 */
const parseXMLFormList = (xmlNode) => {
  const forms = parseXMLList(xmlNode, 'form');
  return sortFormListAlphabetically(forms);
};

/**
 * parse XMLNode with list of drafts into a JS Array
 * @param {XMLNode} xmlNode
 */
const parseXMLDraftList = (xmlNode) => {
  const drafts = parseXMLList(xmlNode, 'draft').map((draft) => {
    return {
      ...draft,
      isDispatch: draft.is_oneshot === 'true',
      resourceVersions: JSON.parse(
        draft.resource_versions.replaceAll('=>', ':')
      ),
    };
  });
  return sortFormListAlphabetically(drafts);
};

/**
 * parse XMLNode with list of draft images into a JS Array
 * @param {XMLNode} xmlNode
 */
const parseXMLDraftImagesList = (xmlNode) => {
  const filesXmlNode = xmlNode.children[0];
  const images = parseXMLList(filesXmlNode, 'image');
  return images;
};

/**
 * Sort list alphabetically (ASC)
 * @param {Array<Object>} formsList
 */
const sortFormListAlphabetically = (formsList) =>
  formsList.sort(
    (a, b) =>
      a.title.localeCompare(b.title) ||
      moment(b.updated_at) - moment(a.updated_at)
  );
